import { SavedSchedule } from "components/widgets/Scheduler/OrganiseScheduleSetting";
import { WidgetNameTypes } from "constants/widgets";
import { LOCALSTORAGE_WIDGET_KEY } from "hooks/widgets";
import { isEmpty, update } from "lodash";
import { createContext, useEffect, useReducer } from "react";
import { DashboardProps, GlobalData, OldGlobalData, OldDashboardProps } from "types/GlobalData";
import {
  buildDashboardGroupsLoadView,
  getValidDashboards,
  resolveActiveDashboardSelection,
  sanitizeDashboardGroups,
} from "util/dashboardGroups";
import { loadData, saveData } from "util/wp-api";
import { v4 as uuidv4 } from "uuid";

interface Props {
  data: GlobalData;
  setData: (data: Partial<GlobalData>) => void;
  onRefreshOldData: () => void;
  wpSaveData: (key: string, data: any) => Promise<void>;
  wpLoadData: (key: string) => Promise<any>;
}

const Key = "global";
const MAX_LOAD_ATTEMPTS = 3;
const LOAD_RETRY_DELAY_MS = 2000;

export const getInitialRememberSettings = () => {
  const data: { [k: string]: any } = {};

  WidgetNameTypes.forEach((name: string) => {
    data[name] = {
      isOn: true,
      settings: {},
    };
  });

  return data;
};

const ID = uuidv4();
export const INITIAL_REMEMBER_SETTINGS = getInitialRememberSettings();
export const INITIAL_DASHBOARD_DATA: DashboardProps[] = [
  {
    id: ID,
    name: "New Slide",
    backgroundName: "Purple",
    fontName: "Lemon Milk",
    rememberSettings: INITIAL_REMEMBER_SETTINGS,
    voiceType: "Female",
  },
];

const GroupID = uuidv4();
export const INITIAL_DASHBOARD_GROUP = [
  {
    id: GroupID,
    name: "New Group",
    dashboards: INITIAL_DASHBOARD_DATA,
  },
];

const DEFAULT_STATE: GlobalData = {
  activeDashboardId: ID,
  activeDashboardGroupId: GroupID,
  savedSchedules: [],
  savedActivities: [],
  saveWords: [],
  assignedEvents: {},
  assignedSchedules: {},
  dashboardGroups: INITIAL_DASHBOARD_GROUP,
  images: [],
  activeImages: [],
  savedGroupConfigurations: [],
  savedLessonPlans: [],
};

function isOldGlobalData(data: any): data is OldGlobalData {
  return !!data?.dashboards;
}

function convertOldDashboardToNew(oldDashboard: OldDashboardProps): DashboardProps {
  return {
    id: oldDashboard.id,
    name: oldDashboard.name,
    backgroundName: oldDashboard.backgroundName,
    fontName: "Lemon Milk",
    rememberSettings: oldDashboard.rememberSettings,
    voiceType: oldDashboard.voiceType,
  };
}

function convertOldToNew(oldData: OldGlobalData): GlobalData {
  let updatedSchedules: { [date: string]: SavedSchedule } = {};
  if (oldData.assignedSchedules) {
    Object.keys(oldData.assignedSchedules).forEach((date) => {
      let [year, month, day] = date.split("-"); // Split the date into year, month, day
      // Pad month and day with leading zeros if necessary
      let formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;

      // Assign the original object to the new formatted date
      updatedSchedules[formattedDate] = oldData.assignedSchedules[date];
    });
  }
  return {
    activeDashboardId: oldData.activeDashboardId,
    activeDashboardGroupId: GroupID,
    savedSchedules: [],
    savedActivities: [],
    assignedSchedules: updatedSchedules,
    assignedEvents: {},
    saveWords: oldData.saveWords,
    dashboardGroups: [
      {
        id: GroupID,
        name: "New Group",
        dashboards: oldData.dashboards.map(convertOldDashboardToNew),
      },
    ],
    images: [],
    activeImages: [],
    savedGroupConfigurations: [],
    savedLessonPlans: [],
  };
}

async function loadSettingsData(attempt = 1): Promise<GlobalData> {
  try {
    const loadedData: any = await loadData(Key);

    if (isOldGlobalData(loadedData)) {
      return updateInitialGlobalParams(convertOldToNew(loadedData as OldGlobalData));
    }

    return updateInitialGlobalParams(loadedData as GlobalData);
  } catch (error) {
    // loadData throws on real failures and returns {} only for a genuinely empty
    // dashboard, so a throw here means the fetch failed. Retry with backoff so a
    // transient error does not leave the dashboard unloaded; give up by re-throwing
    // so the caller knows not to initialize (which would overwrite real data).
    if (attempt < MAX_LOAD_ATTEMPTS) {
      console.warn(
        `DashboardContext: Load failed (attempt ${attempt}/${MAX_LOAD_ATTEMPTS}), retrying...`,
        error,
      );
      await new Promise((resolve) => setTimeout(resolve, LOAD_RETRY_DELAY_MS * attempt));
      return loadSettingsData(attempt + 1);
    }
    throw error;
  }
}

const updateInitialGlobalParams = (data: GlobalData): GlobalData => {
  if (isEmpty(data)) {
    return DEFAULT_STATE;
  }

  // A missing or non-array dashboardGroups is a shape this function cannot
  // normalize, so pass the payload through rather than fabricating a group.
  if (!Array.isArray(data.dashboardGroups)) {
    return { ...data };
  }

  // Lookups below use the sanitized view only. The raw persisted slots are kept so
  // merely opening Digi Dashboard cannot delete data; cleanup happens on drag write.
  const { persistedGroups, validGroups } = buildDashboardGroupsLoadView(data.dashboardGroups);

  const dataKeys = Object.keys(data);
  const defaultKeys = Object.keys(DEFAULT_STATE);

  const groupWithDashboards = validGroups.find((group) => group.dashboards.length > 0);
  const hasDashboards = !!groupWithDashboards;
  const dataRememberSettingsKeys =
    hasDashboards && groupWithDashboards
      ? Object.keys(getValidDashboards(groupWithDashboards.dashboards)[0]?.rememberSettings ?? {})
      : [{ rememberSettings: {} }];
  const defaultRememberSettingsKeys = Object.keys(INITIAL_REMEMBER_SETTINGS);

  if (
    dataKeys.length !== defaultKeys.length ||
    dataRememberSettingsKeys.length !== defaultRememberSettingsKeys.length
  ) {
    const dashboardData = (
      hasDashboards && groupWithDashboards
        ? getValidDashboards(groupWithDashboards.dashboards)
        : INITIAL_DASHBOARD_DATA
    ).map((dashboard) => {
      return {
        ...DEFAULT_STATE,
        ...dashboard,
        rememberSettings: {
          ...INITIAL_REMEMBER_SETTINGS,
          ...dashboard.rememberSettings,
        },
      };
    });

    // Preserve the raw persisted groups (including invalid slots) so this load
    // path cannot delete data; only a missing group list is synthesized.
    const groupData = [...persistedGroups];

    if (!hasDashboards) {
      groupData.push({
        ...DEFAULT_STATE.dashboardGroups[0],
        dashboards: dashboardData,
      });
    }

    return { ...data, dashboardGroups: groupData };
  }

  return { ...data };
};

interface Action {
  type: "update" | "refreshOldData";
  payload: Partial<GlobalData>;
}

function reducer(state: GlobalData, action: Action): GlobalData {
  if (action.type === "update") {
    const globalData = {
      ...state,
      ...action.payload,
    };

    saveData(Key, globalData);

    return globalData;
  }
  // TODO change it after implementing wordpress
  if (action.type === "refreshOldData") {
    saveData(Key, {});
    saveData("WidgetData", {});
  }

  return state;
}

export const useDashboardContext = (): Props => {
  const [state, dispatch] = useReducer(reducer, {} as GlobalData);

  // Function to synchronize widget data with dashboard groups
  const synchronizeWidgetData = async (dashboardGroups: any[]) => {
    try {
      const widgetData = await loadData(LOCALSTORAGE_WIDGET_KEY);
      if (!widgetData) return;

      const allDashboardIds = new Set<string>();

      // Collect all dashboard IDs from all groups. Callers pass the sanitized view,
      // but sanitize again so a raw caller can never dereference a null slot.
      sanitizeDashboardGroups(dashboardGroups).forEach((group) => {
        group.dashboards.forEach((dashboard: DashboardProps) => {
          allDashboardIds.add(dashboard.id);
        });
      });

      // Find orphaned widgets (widgets whose dashboard ID doesn't exist in dashboard groups)
      const orphanedWidgetIds = Object.keys(widgetData).filter((widgetId) => {
        const dashboardId = widgetId.split("|")[1];
        return dashboardId && !allDashboardIds.has(dashboardId);
      });

      if (orphanedWidgetIds.length > 0) {
        console.log(`Found ${orphanedWidgetIds.length} orphaned widgets, cleaning up...`);

        // Remove orphaned widgets
        const cleanedWidgetData = { ...widgetData };
        orphanedWidgetIds.forEach((id) => {
          delete cleanedWidgetData[id];
        });

        // Save cleaned widget data
        await saveData(LOCALSTORAGE_WIDGET_KEY, cleanedWidgetData);
      }
    } catch (error) {
      console.error("Error synchronizing widget data:", error);
    }
  };

  useEffect(() => {
    loadSettingsData()
      .then(async (data) => {
        console.log("DashboardContext: Loading data from WordPress", data);

        // Sanitized view for lookups only. The dispatched payload keeps the raw
        // persisted groups so opening this page never deletes invalid entries.
        const { validGroups } = buildDashboardGroupsLoadView(data.dashboardGroups);

        // Synchronize widget data with dashboard groups
        if (validGroups.length > 0) {
          await synchronizeWidgetData(validGroups);
        }

        // Ensure active dashboard state is properly set. Selection is resolved
        // against the sanitized view so a null slot can never become active.
        if (validGroups.length > 0) {
          const selection = resolveActiveDashboardSelection(
            {
              activeDashboardId: data.activeDashboardId,
              activeDashboardGroupId: data.activeDashboardGroupId,
            },
            validGroups,
          );
          data = { ...data, ...selection };
        }

        console.log("DashboardContext: Final data before dispatch:", data);
        dispatch({
          type: "update",
          payload: data,
        });
      })
      .catch((error) => {
        // A failed load must not be treated as an empty dashboard: dispatching here
        // would persist DEFAULT_STATE over the user's real data via the reducer's save.
        console.error(
          "DashboardContext: Failed to load dashboard data; keeping current state",
          error,
        );
      });
  }, []);

  function updateGlobalData(newData: Partial<GlobalData>) {
    dispatch({
      type: "update",
      payload: newData,
    });
  }

  const handleRefreshOldData = () => {
    dispatch({
      type: "refreshOldData",
      payload: {},
    });
  };

  return {
    data: state,
    setData: updateGlobalData,
    onRefreshOldData: handleRefreshOldData,
    wpSaveData: saveData,
    wpLoadData: loadData,
  };
};

export const DashboardContext = createContext<Props>({
  data: {
    savedSchedules: [],
    savedActivities: [],
    assignedSchedules: {},
    assignedEvents: {},
    saveWords: [],
    activeDashboardId: "",
    dashboardGroups: [],
    activeDashboardGroupId: "",
    images: [],
    activeImages: [],
    savedGroupConfigurations: [],
    savedLessonPlans: [],
  },
  setData: (newData: Partial<GlobalData>) => {},
  onRefreshOldData: () => {},
  wpSaveData: saveData,
  wpLoadData: loadData,
});
